import classNames from 'classnames';
import React, { createRef, useEffect, useState } from 'react';

import { validateEmail } from '../../shared/utils';
import { Button } from '../button/button.component';
import Modal from '../modal/modal.component';
import { Input } from '../text-input/text-input.component';
import styles from './share.module.css';
import { ResultOptionType, ShareMultiSelectProps } from './share.types';
import { AccessDropdown, Footer, TeamMembers, UsersResultsShare } from './sub-components';

export interface ShareComponentProps {
  resultOptions?: ResultOptionType[];
  onClose?: () => void;
}

export const ShareComponent = ({ resultOptions, onClose }: ShareComponentProps) => {
  return (
    <div className={styles['share-component-wrapper']}>
      <Modal.Header title="Invite collaborators" onCloseBtnClick={onClose} className="p-20" />
      <ShareMultiSelect
        resultOptions={resultOptions}
        hasFooter={true}
        hasAccessComponent={true}
        hasTeamMembersSection={true}
      />
    </div>
  );
};

export const ShareMultiSelect = ({
  resultOptions = [],
  hasFooter = false,
  hasAccessComponent = false,
  hasTeamMembersSection = false,
  wrapperClassNames,
}: ShareMultiSelectProps) => {
  const [activeOption, setActiveOption] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const inputRef = createRef<HTMLInputElement>();

  const [searchText, setSearchText] = React.useState('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleOnMouseEnter = (itemIndex: number) => {
    setActiveOption(itemIndex);
  };

  const handleOptionClick = (index: number) => {
    if (selectedOptions.includes(resultOptions[index].username)) {
      return;
    }
    setSelectedOptions([...selectedOptions, resultOptions[index].username]);
  };

  const handleSelectedOptionClick = (index: number) => {
    setSelectedOptions(selectedOptions.filter((option) => option !== selectedOptions[index]));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowUp') {
      if (activeOption === 0) {
        return;
      }
      setActiveOption(activeOption - 1);
    } else if (event.key === 'ArrowDown') {
      if (activeOption === resultOptions.length - 1) {
        return;
      }
      setActiveOption(activeOption + 1);
    } else if (event.key === 'Enter') {
      if (resultOptions.length > 0) {
        if (selectedOptions.includes(resultOptions[activeOption].username)) {
          return;
        }
        setSelectedOptions([...selectedOptions, resultOptions[activeOption].username]);
      } else {
        if (!searchText.length || selectedOptions.includes(searchText)) {
          return;
        }
        setSelectedOptions([...selectedOptions, searchText]);
        setSearchText('');
      }
    } else if (event.key === 'Backspace') {
      if (searchText.length) {
        return;
      }
      setSelectedOptions(selectedOptions.slice(0, selectedOptions.length - 1));
    }
  };
  const MultiSelectClassNames = classNames({
    'px-20 flex items-center py-4 border-b border-gray-100': true,
    [wrapperClassNames || '']: !!wrapperClassNames,
  });
  const accessMenuItems = [
    { title: 'Full', subTitle: 'Can edit, share and manage', value: 'fullAccess' },
    { title: 'Edit', subTitle: 'Can edit but not share with others', value: 'canEdit' },
    { title: 'View', subTitle: 'Can edit but can comment', value: 'canView' },
  ];
  return (
    <>
      <div className={MultiSelectClassNames}>
        <span className="mr-8 text-sm">To: </span>
        <div className="w-full">
          {selectedOptions.map((option, index) => {
            const isInvalid =
              !resultOptions.filter((resultOption) => resultOption.username === option).length &&
              !validateEmail(option);
            return (
              <Button
                key={index}
                status="tertiary"
                className={classNames({
                  [styles['name-tag']]: true,
                  'mr-4': true,
                  [styles['invalid-tag']]: isInvalid,
                })}
                onClick={handleSelectedOptionClick.bind(this, index)}
              >
                {option}
              </Button>
            );
          })}
          <Input
            placeholder="Search..."
            inputClassName="inline-flex placeholder-gray-500"
            wrapperClassName="inline-flex border-none pl-0 pr-0 pt-0 pb-0 text-sm"
            field="search"
            type="text"
            onChange={handleChange}
            value={searchText}
            inputProps={{
              onKeyDown: handleKeyDown,
              ref: inputRef,
            }}
          />
        </div>

        {hasAccessComponent && <AccessDropdown menuItems={accessMenuItems} />}
      </div>
      {hasTeamMembersSection && !resultOptions.length && !searchText.length && <TeamMembers />}
      <div
        className={classNames({
          'max-h-200 overflow-y-auto bg-white w-full': true,
          absolute: !hasFooter,
        })}
      >
        {searchText.length
          ? resultOptions?.map(({ id, username, email, userType }, index) => (
              <UsersResultsShare
                id={id}
                key={id}
                username={username}
                email={email}
                userType={userType}
                isActive={index === activeOption}
                onClick={handleOptionClick.bind(this, index)}
                onMouseEnter={handleOnMouseEnter.bind(this, index)}
              />
            ))
          : null}
      </div>
      {hasFooter && (
        <Footer
          shouldShowPublicLink={!resultOptions.length && !searchText.length}
          hasSelectedOptions={selectedOptions.length !== 0}
        />
      )}
    </>
  );
};
